import omit from 'lodash/omit'
import uuid from '@vend/vend-uuid'

import { ApiRetailerPaymentType } from '@vend/types'
import { vn } from 'vend-number'
import moment from 'moment'
import { GqlSaleAddress } from '../queries/sale_create'
import { SurchargeTenderProperties } from './sale'

export interface ExternalPaymentData {
  source?: string
  source_id?: string
  payment_id?: string
  external_transaction_id?: string
  merchant_id?: string
  reference_id?: string
  signature_url?: string
  signature_base64?: string
  transaction_status?: string
  auth_code?: string
  provider_fields?: Map<string, string>
  card?: ExternalCardData
  emv?: ExternalEmvData
  terminal?: ExternalTerminalData
}

export interface ExternalCardData {
  entry_method?: string
  brand?: string
  type?: string
  last_four?: string
  cardholder_name?: string
}

export interface ExternalEmvData {
  aid?: string
  application_label?: string
  application_preferred_name?: string
  cryptogram_type?: string
  cryptogram?: string
  pin_verified?: boolean
  tags?: EmvTag[]
}

interface EmvTag {
  key: string
  value: string
}

export interface ExternalTerminalData {
  serial_number?: string
  model?: string
}

export interface Card {
  entryMethod?: string
  brand?: string
  type?: string
  lastFour?: string
  cardholderName?: string
}

export interface Terminal {
  serialNumber?: string
  model?: string
}

export interface EMV {
  aid?: string
  applicationLabel?: string
  applicationPreferredName?: string
  cryptogramType?: string
  cryptogram?: string
  pinVerified?: boolean
  tags?: EMVTag[]
}

interface EMVTag {
  key: string
  value: string
}

/**
 * Represents a payment on a sale.
 */
export interface Payment {
  /**
   * The unique ID of this Payment locally generated on the client.
   *
   * @type UUID
   */
  localId: string
  id?: string
  registerId: string
  registerSequenceId: string
  paymentType: ApiRetailerPaymentType
  amount: number | string
  tipAmount?: number | string
  surchargeData?: SurchargeTenderProperties
  paymentDate: string
  source?: string
  sourceId?: string
  // tslint:disable-next-line
  external_transaction_id?: string
  merchantId?: string
  referenceId?: string
  authCode?: string
  signatureURL?: string
  signatureBase64?: string
  transactionStatus?: string
  card?: Card
  emv?: EMV
  terminal?: Terminal
  providerFields?: Map<string, string>
  changeAmount?: number | string
  billingAddress?: GqlSaleAddress

  /**
   * Identify if the payment is part of an API response. Allows for payments to be excluded when calculating
   * store credit and loyalty balances.
   */
  retrieved: boolean
}

export type PaymentJson = Omit<Payment, 'paymentType'> & {
  payment_type_id: string
}

/**
 * Create a payment based on the given info.
 *
 * @param  amount {Number}
 *         The total amount of the payment.
 *
 * @param  paymentType {Object}
 *         The payment type used to process the sale.
 *
 * @param  registerId {String}
 *         The ID of the register that processed this payment.
 *
 * @param  registerSequenceId {String}
 *         The ID of the active register open sequence at the time payment was taken.
 *
 * @param {Object} [transactionData]
 *
 *        @param {String} transactionData.source
 *               The descriptive identifier for the transaction, e.g. "Vend Gift Card"
 *
 *        @param {String} transactionData.source_id
 *               The source id for the payment transaction
 *
 *        @param {String} [transactionData.payment_id]
 *               The id for the payment transaction
 *
 *        @param {String} [transactionData.external_transaction_id]
 *               The external id for the payment transaction
 *
 *        @param {String} [transactionData.merchant_id]
 *               The merchant id for the retailer
 *
 *        @param {String} [transactionData.reference_id]
 *               The reference id for the integrator
 *
 *        @param {String} [transactionData.signature_url]
 *               The signature url for the digital signature
 *
 *        @param {String} [transactionData.signature_base64]
 *               The signature base64 for the digital signature base64 data
 *
 *        @param {String} [transactionData.transaction_status]
 *               The status of the transaction. It will be 'APPROVED_SIGNATURE' or 'undefined'
 *
 *        @param {String} [transactionData.auth_code]
 *               The auth code for authorization code provided by authorization network
 *
 *        @param {Object} [transactionData.card]
 *               The card for the payment card detail
 *
 *        @param {Object} [transactionData.terminal]
 *               The terminal for the payment terminal detail
 *
 *        @param {Object} [transactionData.emv]
 *               The emv for the payment card emv detail
 *
 * @param  {String} paymentId
 *         The id of the current payment.
 *
 * @param  {number | string} [changeAmount]
 *         The amount of change given to the customer (for cash payments where overpayment occurs).
 */
export function createPayment(
  amount: number | string,
  paymentType: ApiRetailerPaymentType,
  registerId: string,
  registerSequenceId: string,
  transactionData?: ExternalPaymentData,
  paymentId?: string,
  tipAmount?: number | string,
  surchargeData?: SurchargeTenderProperties,
  changeAmount?: number | string
): Payment {
  const payment: Payment = {
    localId: paymentId || uuid(),
    registerId,
    registerSequenceId,
    paymentType,
    amount,
    paymentDate: moment().format(),
    tipAmount,
    surchargeData,
    changeAmount,
    retrieved: false,
  }

  if (transactionData) {
    payment.source = transactionData.source
    payment.sourceId = transactionData.source_id

    if (transactionData.payment_id) {
      payment.id = transactionData.payment_id
    }
    if (transactionData.external_transaction_id) {
      payment.external_transaction_id = transactionData.external_transaction_id
    }
    if (transactionData.merchant_id) {
      payment.merchantId = transactionData.merchant_id
    }
    if (transactionData.reference_id) {
      payment.referenceId = transactionData.reference_id
    }
    if (transactionData.auth_code) {
      payment.authCode = transactionData.auth_code
    }
    if (transactionData.signature_url) {
      payment.signatureURL = transactionData.signature_url
    }
    if (transactionData.signature_base64) {
      payment.signatureBase64 = transactionData.signature_base64
    }
    if (transactionData.transaction_status) {
      payment.transactionStatus = transactionData.transaction_status
    }

    const { card, emv, terminal, provider_fields } = transactionData
    if (card) {
      payment.card = getPaymentCard(card)
    }
    if (emv) {
      payment.emv = getPaymentEMV(emv)
    }
    if (terminal) {
      payment.terminal = getPaymentTerminal(terminal)
    }

    if (provider_fields) {
      payment.providerFields = provider_fields
    }
  }

  return payment
}

type ChangePaymentInput = Pick<
  Payment,
  'amount' | 'paymentType' | 'registerId' | 'registerSequenceId'
>

/**
 * Create a payment to represent the change on a sale.
 *
 * @param  amount {Number}
 *         The total amount of the change payment.
 *
 * @param  paymentType {Object}
 *         The payment type used.
 *
 * @param  registerId {String}
 *         The ID of the register that processed this payment.
 *
 * @param  registerSequenceId {String}
 *         The ID of the active register open sequence at the time payment was taken.
 */
export function createChangePayment({
  amount,
  paymentType,
  registerId,
  registerSequenceId,
}: ChangePaymentInput): Payment {
  return {
    localId: uuid(),
    amount,
    paymentDate: moment().format(),
    paymentType,
    registerId,
    registerSequenceId,
    retrieved: false,
  }
}

/**
 * Returns a JSON representation of the given payment suitable for storing in IndexedDB. The referenced
 * payment type is replaced with its respective ID.
 *
 * @return JSON representation of the payment
 */
export function paymentToJSON(payment: Payment): PaymentJson {
  const data = { ...payment } // Keeps only enumerable properties (excludes prototype)
  return {
    ...omit<typeof data, 'paymentType'>(data, 'paymentType'),
    payment_type_id: data.paymentType.id,
  }
}

/**
 * Returns true if the given payment includes a positive tip amount.
 *
 * @return true if the payment has a tip
 */
export function paymentHasTip(payment: Payment): boolean {
  return !!payment.tipAmount && vn(payment.tipAmount).gt(0)
}

/**
 * Get the payment card from transaction data.
 *
 * @param {Object} [data]
 *        The card for the payment card detail
 *
 *        @param {String} [data.entry_method]
 *               The entry method for the card
 *
 *        @param {String} [data.brand]
 *               The brand for the payment card
 *
 *        @param {String} [data.type]
 *               The type for the payment card
 *
 *        @param {String} [data.last_four]
 *               The last four numbers for the payment card
 *
 *        @param {String} [data.cardholder_name]
 *               The cardholder name for payment card
 *
 * @returns {Card}
 */
function getPaymentCard(data: ExternalCardData) {
  return {
    entryMethod: data.entry_method,
    brand: data.brand,
    type: data.type,
    lastFour: data.last_four,
    cardholderName: data.cardholder_name,
  }
}

/**
 * Get the payment EMV from transaction data.
 *
 * @param {Object} [transactionData.emv]
 *        The emv for the payment card emv detail
 *
 *        @param {String} [transactionData.emv.aid]
 *               The aid for the emv
 *
 *        @param {String} [transactionData.emv.application_label]
 *               The application label for the emv
 *
 *        @param {String} [transactionData.emv.cryptogram_type]
 *               The cryptogram type for the emv
 *
 *        @param {String} [transactionData.emv.cryptogram]
 *               The cryptogram for the emv
 *
 *        @param {Boolean} [transactionData.emv.pin_verified]
 *               The pin verified for the pin
 *
 *        @param {Array} [transactionData.emv.tags]
 *               The Tags for the emv
 *
 * @returns {EMV}
 */
function getPaymentEMV(data: ExternalEmvData) {
  return {
    aid: data.aid,
    applicationLabel: data.application_label,
    applicationPreferredName: data.application_preferred_name,
    cryptogramType: data.cryptogram_type,
    cryptogram: data.cryptogram,
    pinVerified: data.pin_verified,
    tags: data.tags,
  }
}

/**
 * Get the payment terminal from transaction data.
 *
 * @param {Object} [data]
 *        The terminal for the payment terminal detail
 *
 *        @param {String} [data.serial_number]
 *               The serial number for the payment terminal
 *
 *        @param {String} [data.model]
 *               The mode for the payment terminal
 *
 * @returns {Terminal}
 */
function getPaymentTerminal(data: ExternalTerminalData) {
  return {
    serialNumber: data.serial_number,
    model: data.model,
  }
}

export const testables = {
  getPaymentCard,
  getPaymentEMV,
  getPaymentTerminal,
}
